import type { MouseEvent, ReactNode } from 'react';

export type DataTableCellValue = string | number | boolean | null | undefined;

export type FilterOption = {
    label: string;
    value: DataTableCellValue;
};

export interface DataTableColDef {
    field: string;
    headerName: string;
    /**
     * Fixed pixel width. The column never flexes — it stays at this width
     * across viewport sizes. Use for content with known shape (UUIDs, badges,
     * fixed-format IDs). Mutually exclusive with `flex`.
     */
    width?: number;
    /**
     * Flex weight (like CSS `fr` units). The column absorbs a share of
     * leftover container space after fixed-width columns are subtracted,
     * proportional to its weight vs. other flex columns. Use for variable-
     * content text (names, emails, addresses). Falls back to `flex: 1` if
     * neither `width` nor `flex` is set.
     */
    flex?: number;
    /** Minimum px the column can shrink to. Defaults to 80. */
    minWidth?: number;
    /** Maximum px the column can grow to. Defaults to no maximum. */
    maxWidth?: number;
    /** When true, cell text wraps and the row height grows to fit content. */
    wrapText?: boolean;
    sortable?: boolean;
    copyable?: boolean;
    filterOptions?: FilterOption[];
    /**
     * Custom labels for sort directions in the column header dropdown menu.
     * Defaults to "Ascending" / "Descending" (i18n'd).
     * Use for contextual labels like "Older to newer" / "Newer to older".
     */
    sortLabels?: { asc: string; desc: string };
    valueFormatter?: (value: DataTableCellValue, row: DataTableRow) => string;
    /**
     * Custom React renderer for the cell body. When provided, replaces the
     * default text rendering and disables the copy-to-clipboard affordance for
     * the cell. Use for badges, chips, or other non-textual content.
     */
    renderCell?: (row: DataTableRow) => ReactNode;
    /**
     * When true, drops the vertical divider on the right edge of the cell.
     * Use to visually join this column with the next one (e.g. a count badge
     * that should read as part of the leading row affordance).
     */
    noBorderRight?: boolean;
    onCellClick?: (row: DataTableRow) => void;
    /** Tooltip text for non-copyable clickable cells (e.g. "Click to download"). */
    tooltipText?: string;
    /**
     * When true, sorting for this column is applied client-side on the loaded
     * rows instead of being sent to the server. Use for columns whose data is
     * joined client-side from a lookup query and whose field name the server
     * does not recognise.
     */
    clientSort?: boolean;
}

export type DataTableRow = Record<string, DataTableCellValue> & {
    id: string | number;
};

export type OnRowClick = (row: DataTableRow) => void;

// Per-column resize plumbing shared by head + body cells.
export interface DataTableCellShell {
    column: DataTableColDef;
    isFirst: boolean;
    isLast: boolean;
    /** Computed pixel width for this column (post-distribution). */
    width: number;
    isResizeActive: boolean;
    onResizeStart: (field: string, e: MouseEvent) => void;
    onResizeHover: (field: string | null) => void;
}

export enum SortDirection {
    ASC = 'asc',
    DESC = 'desc',
}

export interface DataTableSort {
    field: string;
    direction: SortDirection;
}

export interface DataTableState {
    // Ordered list of sort keys: the first wins, ties break on the second,
    // and so on. Empty array ≡ rows render in their incoming order.
    sorts: DataTableSort[];
    // Absent key ≡ "all selected" (no constraint).
    // Present with [] ≡ "none selected" (zero rows match).
    filters: Record<string, DataTableCellValue[]>;
}

export const DEFAULT_DATA_TABLE_STATE: DataTableState = {
    sorts: [],
    filters: {},
};

// Controlled-mode query state surfaced to the page. Sort/filter/search live in
// the parent so they can drive a server query.
export interface DataTableQueryState {
    sorts: DataTableSort[];
    filters: Record<string, DataTableCellValue[]>;
    search: string;
}

export const DEFAULT_DATA_TABLE_QUERY_STATE: DataTableQueryState = {
    sorts: [],
    filters: {},
    search: '',
};

export const toCellString = (value: DataTableCellValue): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    return String(value);
};
